
import { Environment } from './Environment';
import { IContentOrganizer, IContentOrganizerCreate, IContentOrganizerHubSite, ISharePointSiteInformation } from "./../interfaces/IContentOrganizerCreate";
import { IDurableFunctionStatus } from '../interfaces/IDurableFunctionStatus';
import { IContentOrganizerCustomStatus } from '../interfaces/IContentOrganizerCustomStatus';
import { IContentMap } from '../interfaces/IContentMap';
import { ICreatePageFromTemplateResponse } from '../interfaces/ICreatePageFromTemplateResponse';
import { ICreatePageFromTemplate } from '../interfaces/ICreatePageFromTemplate';
import { IMessageFilter } from '../interfaces/IMessageFilter';
import { FilterService } from '../components/messages/Filter.service';
import { IContentOrganizerSearchResult } from '../interfaces/IContentOrganizerSearchResult';
import { IUpdateContentOrganizerFeaturedImage } from '../interfaces/IUpdateContentOrganizerFeaturedImage';
import { IContentOrganizerCheckPermissionsResult } from '../interfaces/IContentOrganizerCheckPermissionsResult';
import { IDocumentLibraryOption } from '../interfaces/IDocumentLibrary';
import { IContentOrganizerHealthStatus } from '../interfaces/IContentOrganizerHealthStatus';
import { FetchProxy } from 'intraactive-sdk-helper';
import { sdkContainerInstance } from '../global/Container';
import { IEnvironment } from '../interfaces/IEnvironment';
import { IContentOrganizerSharepointSite } from '../interfaces/IContentOrganizerSharepointSite';
import { Helper } from '../Helper';

export default class ContentOrganizerService {

  public static isAddressAvailable(environment: IEnvironment, token: string, tenant: string, siteName: string): Promise<boolean> {
    return new Promise((resolve) => {
      const url = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/sites/${siteName}/available`;
      return new FetchProxy(url, {
        method: "GET",
        headers: new Headers({
          "AuthToken": token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        })
      }).withContainer(sdkContainerInstance)
        .fetch()
        .then((response) => {
          if (!response.ok) {
            throw (response.status);
          } else {
            return response.json();
          }
        }).then(response => {
          resolve(response.value);
        }).catch((error) => {
          console.log(error);
        });
    });
  }

  public static createContentOrganizer(environment: IEnvironment, token: string, tenant: string, contentOrganizer: IContentOrganizerCreate): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (environment && tenant && token) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance`;
        const body: string = JSON.stringify(contentOrganizer);
        new FetchProxy(apiUrl, {
          method: "POST",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }),
          body: body
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
            } else {
              resolve(response.ok);
            }
          }).catch(error => {
            console.log(error);
            reject(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static checkPermissionsForCurrentUser(environment: IEnvironment, token: string, tenant: string): Promise<IContentOrganizerCheckPermissionsResult> {
    return new Promise((resolve) => {
      if (environment && token && tenant) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/check/permissions`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return null;
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });

  }

  public static getContentOrganizerStatus(environment: IEnvironment, token: string, tenant: string, instanceName: string): Promise<IDurableFunctionStatus<IContentOrganizerCustomStatus>> {
    return new Promise((resolve) => {
      if (environment && token && tenant && instanceName) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance/${instanceName}/status`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              if (response.status === 204) {
                return undefined;
              } else {
                return response.json();
              }
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getContentOrganizerHealthStatus(environment: IEnvironment, token: string, tenant: string, instanceName: string): Promise<IContentOrganizerHealthStatus> {
    return new Promise((resolve) => {
      if (environment && token && tenant && instanceName) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance/${instanceName}/health`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            }
            return response.json();
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static syncContentOrganizerSiteUrl(environment: IEnvironment, token: string, tenant: string, instanceName: string, newSiteUrl: string) {
    return new Promise((resolve) => {
      if (environment && token && tenant && instanceName) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant}/instance/${instanceName}`;
        const body = JSON.stringify({
          newSiteCollection: newSiteUrl
        });
        new FetchProxy(apiUrl, {
          method: "PUT",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin",
          body: body
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            }
            return response;
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getSites(tenant: string, token: string, environment: IEnvironment, searchQuery: string, take?: number, skip?: number): Promise<{ searchQuery: string, totalItems: number, items: IContentOrganizerSharepointSite[] }> {
    Helper.setLocalStorage("contentOrganizerWizard_SearchQuery", searchQuery);
    return new Promise<any>((resolve: (sites: any) => void, reject: (error: any) => void): void => {
      let apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/sites?`;

      if (searchQuery) {
        apiUrl += `&searchQuery=${searchQuery}`;
      }
      if (take !== undefined) {
        apiUrl += `&take=${take}`;
      }
      if (skip !== undefined) {
        apiUrl += `&skip=${skip}`;
      }
      new FetchProxy(apiUrl, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'AuthToken': token
        },
        credentials: "same-origin"
      }).withContainer(sdkContainerInstance)
        .fetch()
        .then((response) => {
          if (!response.ok) {
            reject(response);
            return;
          }
          return response.json().then((result) => {
            if (result.searchQuery === localStorage.getItem("contentOrganizerWizard_SearchQuery") || result.searchQuery === null && localStorage.getItem("contentOrganizerWizard_SearchQuery") === "") {
              resolve(result);
            }
          });
        }).catch(error => {
          console.log(error);
          reject(error);
        });
    });
  }

  public static getSiteInformation(environment: IEnvironment, token: string, tenantName: string, siteName: string): Promise<ISharePointSiteInformation> {
    return new Promise((resolve) => {
      const emptyValue = {} as ISharePointSiteInformation;
      if (environment && token && tenantName && siteName) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenantName.replace(".sharepoint.com", "")}/sites/${siteName}`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return emptyValue;
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
            resolve(emptyValue);
          });
      } else {
        resolve(emptyValue);
      }
    });
  }

  public static getSiteDocumentLibraries(environment: IEnvironment, token: string, tenantName: string, siteCollectionAddress: string): Promise<IDocumentLibraryOption[]> {
    return new Promise((resolve) => {
      const emptyValue = [] as IDocumentLibraryOption[];
      if (environment && token && tenantName && siteCollectionAddress) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenantName.replace(".sharepoint.com", "")}/sites/${siteCollectionAddress}/doclibs`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return emptyValue;
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
            resolve(emptyValue);
          });
      } else {
        resolve(emptyValue);
      }
    });
  }

  public static getPages(tenant: string, token: string, environment: IEnvironment, searchQuery: string, take?: number, skip?: number, filter?: IMessageFilter): Promise<any> {
    Helper.setLocalStorage("contentOrganizerWizard_SearchQuery", searchQuery);
    return new Promise<any>((resolve: (sites: any) => void, reject: (error: any) => void): void => {
      const filterQueryString: string = !filter && filter == null ? "" : FilterService.parseFilterDataToQueryString(filter);
      let apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/pages?`;

      if (searchQuery) {
        apiUrl += `&searchQuery=${searchQuery}`;
      }
      if (take !== undefined) {
        apiUrl += `&take=${take}`;
      }
      if (skip !== undefined) {
        apiUrl += `&skip=${skip}`;
      }
      if (filterQueryString.length > 0) {
        apiUrl += filterQueryString;
      }
      new FetchProxy(apiUrl, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'AuthToken': token
        },
        credentials: "same-origin"
      }).withContainer(sdkContainerInstance)
        .fetch()
        .then((response) => {
          if (!response.ok) {
            reject(response);
            return;
          }
          return response.json().then((result) => {
            if (result.searchQuery === localStorage.getItem("contentOrganizerWizard_SearchQuery") || result.searchQuery === null && localStorage.getItem("contentOrganizerWizard_SearchQuery") === "") {
              resolve(result);
            }
          });
        }).catch(error => {
          console.log(error);
          reject(error);
        });
    });
  }

  public static getContentOrganizerInstanceSettings(environment: IEnvironment, token: string, tenant: string, instanceName: string): Promise<IContentOrganizer> {
    return new Promise((resolve) => {
      if (environment && token && tenant && instanceName) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance/${instanceName}`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              if (response.status === 204) {
                return undefined;
              } else {
                return response.json();
              }
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }


  public static getContentOrganizerInstancesList(environment: IEnvironment, token: string, tenant: string, trueOrFalse, searchQuery?: string): Promise<IContentOrganizerCreate[]> {
    return new Promise((resolve) => {
      if (environment && token && tenant) {
        let apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instances/${trueOrFalse}`;
        if (searchQuery) {
          apiUrl += `?searchQuery=${searchQuery}`;
        }
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              if (response.status === 204) {
                return undefined;
              } else {
                return response.json();
              }
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getContentOrganizerSelectedInstances(environment: IEnvironment, token: string, tenant: string): Promise<IContentOrganizerCreate[]> {
    return new Promise((resolve) => {
      if (environment && token && tenant) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instances/false`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              if (response.status === 204) {
                return undefined;
              } else {
                return response.json();
              }
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getContentMap(environment: IEnvironment, token: string, tenant: string, instanceName: string, targeting: boolean): Promise<IContentMap> {
    return new Promise((resolve) => {
      if (environment && token && tenant && instanceName) {
        const apiUrl = `${Environment.getEnvironmentForMainAPI(environment)}/api/${tenant}/contentmap?instanceName=${instanceName}&targeting=${targeting}`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return response.status;
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static saveContentMap(environment: IEnvironment, token: string, tenant: string, instanceName: string, contentMap: IContentMap): Promise<void> {
    return new Promise((resolve, reject) => {
      if (environment && token && tenant && instanceName && contentMap) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance/${instanceName}/contentmap`;
        const body: string = JSON.stringify(contentMap);
        new FetchProxy(apiUrl, {
          method: "PUT",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }),
          body: body
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
            } else {
              resolve();
            }
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static updateFeaturedImage(environment: IEnvironment, token: string, tenant: string, instanceName: string, featuredImage: IUpdateContentOrganizerFeaturedImage): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (environment && token && tenant && instanceName && featuredImage) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance/${instanceName}/update/image`;
        const body: string = JSON.stringify(featuredImage);
        new FetchProxy(apiUrl, {
          method: "PUT",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }),
          body: body
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
            } else {
              resolve(true);
            }
          }).catch(error => {
            console.log(error);
            reject();
          });
      } else {
        reject();
      }
    });
  }

  public static createPageFromTemplate(environment: IEnvironment, token: string, tenant: string, instanceName: string, page: ICreatePageFromTemplate): Promise<ICreatePageFromTemplateResponse> {
    return new Promise((resolve, reject) => {
      if (environment && token && tenant && instanceName && page) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance/${instanceName}/pages`;
        const body: string = JSON.stringify(page);
        new FetchProxy(apiUrl, {
          method: "POST",
          credentials: "same-origin",
          mode: "cors",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }),
          body: body
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
            } else {
              return response.json();
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static deletePage(environment: IEnvironment, token: string, tenant: string, instanceName: string, pageId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (environment && token && tenant && instanceName && pageId) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/instance/${instanceName}/pages/${pageId}`;
        new FetchProxy(apiUrl, {
          method: "DELETE",
          headers: new Headers({
            "AuthToken": token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          })
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
            } else {
              resolve();
            }
          }).catch(error => {
            console.log(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  public static getHubSites(environment: IEnvironment, token: string, tenantName: string): Promise<IContentOrganizerHubSite[]> {
    return new Promise((resolve) => {
      const emptyValue = [];
      if (environment && token && tenantName) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenantName.replace(".sharepoint.com", "")}/hubsites`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return emptyValue;
            } else {
              if (response.status === 204) {
                return emptyValue;
              } else {
                return response.json();
              }
            }
          }).then(response => {
            resolve(response);
          }).catch(error => {
            console.log(error);
            resolve(emptyValue);
          });
      } else {
        resolve(emptyValue);
      }
    });
  }

  public static searchInContentOrganizers(
    environment: IEnvironment,
    token: string,
    tenantName: string,
    contentOrganizerInstances: string[],
    searchQuery: string,
    take: number,
    skip: number): Promise<IContentOrganizerSearchResult> {
    return new Promise((resolve) => {
      const emptyValue: IContentOrganizerSearchResult = { resultItems: [], rowCount: 0, totalRows: 0 };
      if (environment && token && tenantName) {
        const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenantName.replace(".sharepoint.com", "")}/search?instances=${contentOrganizerInstances.join(',')}&searchQuery=${encodeURIComponent(searchQuery)}&take=${take}&skip=${skip}`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'AuthToken': token
          },
          credentials: "same-origin"
        }).withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              return emptyValue;
            } else {
              if (response.status === 204) {
                return emptyValue;
              } else {
                return response.json();
              }
            }
          })
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            console.log(error);
            resolve(emptyValue);
          });
      } else {
        resolve(emptyValue);
      }
    });
  }

  public static downloadExcelExportPages(environment: IEnvironment, token: string, tenant: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const apiUrl = `${Environment.getEnvironmentContentOrganizerAPI(environment)}/${tenant.replace(".sharepoint.com", "")}/pages/export?take=2000&skip=0&IADraft=0`;
      new FetchProxy(apiUrl, {
        method: "GET",
        credentials: "same-origin",
        mode: "cors",
        headers: new Headers({
          "AuthToken": token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        })
      }).withContainer(sdkContainerInstance)
        .fetch()
        .then((response =>
          response.blob()))
        .then((data) => {
          var url = window.URL.createObjectURL(data);
          var a = document.createElement('a');
          a.href = url;
          a.download = `IntraActiveGovernance.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          resolve();
        }).catch(error => {
          console.log(error);
          reject();
        });
    });
  }

}